import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

// ============================================================================
// Types
// ============================================================================

interface WishlistUIState {
  items: string[]; // Product IDs stored locally for optimistic UI
  lastSyncedAt: number | null;
}

interface WishlistUIStore extends WishlistUIState {
  addItem: (productId: string) => void;
  removeItem: (productId: string) => void;
  isInWishlist: (productId: string) => boolean;
  syncWithBackend: (productIds: string[]) => void;
  reset: () => void;
}

// ============================================================================
// Initial State
// ============================================================================

const initialState: WishlistUIState = {
  items: [],
  lastSyncedAt: null,
};

// ============================================================================
// Store
// ============================================================================

/**
 * Wishlist UI Store
 * 
 * Manages ONLY local wishlist UI state with:
 * - localStorage persistence for guests
 * - Optimistic updates for better UX
 * - Sync tracking with backend
 * 
 * Note: Server communication is handled by useWishlist hook (React Query)
 * This store should NEVER make HTTP requests directly
 */
export const useWishlistUIStore = create<WishlistUIStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        /**
         * Add a product to the local wishlist
         * Used for optimistic updates before server confirmation
         */
        addItem: (productId: string) => {
          if (get().isInWishlist(productId)) {
            return;
          }

          set(
            (state) => ({
              items: [...state.items, productId],
            }),
            false,
            'wishlist/addItem'
          );
        },

        /**
         * Remove a product from the local wishlist
         * Used for optimistic updates before server confirmation
         */
        removeItem: (productId: string) => {
          set(
            (state) => ({
              items: state.items.filter((id) => id !== productId),
            }),
            false,
            'wishlist/removeItem'
          );
        },

        /**
         * Check if a product is in the local wishlist
         */
        isInWishlist: (productId: string) => {
          return get().items.includes(productId);
        },

        /**
         * Sync local state with backend data
         * Called after fetching wishlist from server via React Query
         */
        syncWithBackend: (productIds: string[]) => {
          set(
            {
              items: productIds,
              lastSyncedAt: Date.now(),
            },
            false,
            'wishlist/syncWithBackend'
          );
        },

        /**
         * Reset store to initial state
         */
        reset: () =>
          set(
            initialState,
            false,
            'wishlist/reset'
          ),
      }),
      {
        name: 'wishlist-storage',
        partialize: (state) => ({
          items: state.items,
          lastSyncedAt: state.lastSyncedAt,
        }),
      }
    ),
    { name: 'WishlistUIStore' }
  )
);
